import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownAZ, ArrowLeft, FolderSearch, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { TooltipProvider } from "@/components/ui/tooltip";
import { DocumentPicker, type PickerDocument } from "@/components/documents/DocumentPicker";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  fetchLineageVersionStats,
  isAddAsVersionAvailable,
  type AddAsVersionMode,
} from "@/stores/documents/repository";
import { useProjects } from "../../hooks/useProjects";
import { isBlockedFileType, isIgnoredFile } from "../../constants/fileTypes";
import {
  lineageRowsKey,
  lineageStatsKey,
  liveVersionSpan,
  stagedItemName,
  versionHistoryKey,
  versionSpan,
  type AddVersionsOutcome,
  type AddVersionsTarget,
  type StagedVersionItem,
} from "../../types/addVersions";
import { StagedVersionRow } from "../molecules/StagedVersionRow";
import { cn } from "@/lib/utils";

interface AddNewVersionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The document the new versions are stacked onto. */
  targetDocument: AddVersionsTarget | null;
  /** The building the target lives in — the picker browses its projects. */
  buildingId: string;
  /**
   * Commits the staged items in the exact order they should be versioned and
   * reports how many leading items landed. The dialog drops those and keeps
   * the rest staged.
   */
  onConfirm: (items: StagedVersionItem[]) => Promise<AddVersionsOutcome>;
  /** Disables interaction while the batch is in flight. */
  isSubmitting?: boolean;
  /** Set when reached from the Versioning dialog: offers the way back. */
  onBack?: () => void;
  /** Reports how many items are staged, so the host can keep them alive. */
  onStagedChange?: (count: number) => void;
}

/** Natural-order comparator so `rev_2` sorts before `rev_10`. */
const naturalCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

/** The lineage a staged existing document belongs to. */
function lineageKeyOf(document: { id: string; versionGroup: string | null }): string {
  return document.versionGroup ?? `doc:${document.id}`;
}

let keySequence = 0;
const nextKey = (prefix: string) => {
  keySequence += 1;
  return `${prefix}-${keySequence}`;
};

/**
 * Stage uploads and existing documents as consecutive new versions of one
 * document.
 *
 * Items commit in list order, so the list *is* the version assignment: the
 * projected version number(s) render against each row and the last row is
 * marked as the one that becomes current. Order is never inferred silently —
 * the browser does not guarantee the order of `input.files`, and a picked
 * document lands where the user puts it.
 *
 * An existing document is either MOVED — its whole history is appended and it
 * leaves its old place — or COPIED, one new version sharing its bytes. Only a
 * lineage's current version can be moved.
 */
export function AddNewVersionsDialog({
  open,
  onOpenChange,
  targetDocument,
  buildingId,
  onConfirm,
  isSubmitting = false,
  onBack,
  onStagedChange,
}: AddNewVersionsDialogProps) {
  const { t } = useLanguage();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { projects } = useProjects(buildingId);
  const [items, setItems] = useState<StagedVersionItem[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const copy = t.documents.versioning.addNewVersion;
  const targetVersionGroup = targetDocument?.versionGroup ?? null;

  // Numbering starts from the lineage's true maximum — trashed versions keep
  // their numbers — not from the head row's own version.
  const { data: targetStats } = useQuery({
    queryKey: lineageStatsKey(targetVersionGroup ?? ""),
    queryFn: () => fetchLineageVersionStats(targetVersionGroup ?? ""),
    enabled: open && targetVersionGroup !== null,
    staleTime: 0,
  });
  const baseVersion = targetStats?.maxVersion ?? targetDocument?.version ?? 0;

  // Staged items belong to one target; never carry them into the next one.
  // Keyed on the target, not on `open`: "Back to versions" hides this dialog
  // with the target still set, and must not drop what the user staged. The
  // host clears the target when the dialogs close.
  const targetId = targetDocument?.id ?? null;
  useEffect(() => {
    setItems([]);
  }, [targetId]);
  useEffect(() => {
    onStagedChange?.(items.length);
  }, [items.length, onStagedChange]);
  useEffect(() => {
    if (!open) {
      setIsDragOver(false);
      setPickerOpen(false);
    }
  }, [open]);

  const canChooseExisting =
    targetVersionGroup !== null &&
    targetDocument?.parentDocumentId == null &&
    isAddAsVersionAvailable();

  /**
   * Drop OS junk silently, reject blocked types loudly, append the rest.
   * Mirrors the gating the bulk and single upload paths already apply.
   */
  const addFiles = useCallback(
    (incoming: File[]) => {
      const candidates = incoming.filter((f) => !isIgnoredFile(f));
      const blocked = candidates.filter(isBlockedFileType);
      const accepted = candidates.filter((f) => !isBlockedFileType(f));

      if (blocked.length > 0) {
        toast({
          variant: "destructive",
          title: copy.blockedTitle,
          description: copy.blockedDescription.replace("{count}", String(blocked.length)),
        });
      }

      if (accepted.length > 0) {
        setItems((prev) => [
          ...prev,
          ...accepted.map<StagedVersionItem>((file) => ({
            kind: "upload",
            key: nextKey("upload"),
            file,
          })),
        ]);
      }
    },
    [copy.blockedDescription, copy.blockedTitle, toast]
  );

  /** Read how many versions moving a lineage would bring, onto its row. */
  const loadLineage = useCallback(
    async (key: string, versionGroup: string) => {
      try {
        const stats = await queryClient.fetchQuery({
          queryKey: lineageStatsKey(versionGroup),
          queryFn: () => fetchLineageVersionStats(versionGroup),
          staleTime: 0,
        });
        setItems((prev) =>
          prev.map((item) =>
            item.key === key && item.kind === "existing"
              ? {
                  ...item,
                  lineage: { rowCount: stats.rowCount, liveCount: stats.liveCount },
                  lineageFailed: false,
                }
              : item
          )
        );
      } catch {
        setItems((prev) =>
          prev.map((item) =>
            item.key === key && item.kind === "existing"
              ? { ...item, lineage: { rowCount: 1, liveCount: 1 }, lineageFailed: true }
              : item
          )
        );
      }
    },
    [queryClient]
  );

  const handlePicked = (docs: PickerDocument[], pickedProjectId: string | null) => {
    setPickerOpen(false);
    const staged = new Set(items.flatMap((i) => (i.kind === "existing" ? [i.document.id] : [])));
    const lineagesInList = new Set(
      items.flatMap((i) => (i.kind === "existing" ? [lineageKeyOf(i.document)] : []))
    );
    const added: StagedVersionItem[] = [];
    for (const doc of docs) {
      if (staged.has(doc.id)) continue;
      const document = {
        id: doc.id,
        filename: doc.filename,
        version: doc.version ?? null,
        versionGroup: doc.version_group ?? null,
        projectId: doc.project_id ?? (pickedProjectId !== null ? Number(pickedProjectId) : null),
        isCurrentVersion: doc.is_latest_version !== false,
      };
      const lineage = lineageKeyOf(document);
      // Move is the default for a current version whose history is not in the
      // list yet — that is the "this file is really the next revision" case.
      // An older version, or a second pick from the same history, can only be
      // copied.
      const mode: AddAsVersionMode =
        document.isCurrentVersion && !lineagesInList.has(lineage) ? "move" : "copy";
      lineagesInList.add(lineage);
      staged.add(doc.id);
      added.push({
        kind: "existing",
        key: nextKey("existing"),
        mode,
        document,
        lineage: document.versionGroup === null ? { rowCount: 1, liveCount: 1 } : null,
      });
    }
    if (added.length === 0) return;
    setItems((prev) => [...prev, ...added]);
    for (const item of added) {
      if (item.kind === "existing" && item.document.versionGroup !== null) {
        void loadLineage(item.key, item.document.versionGroup);
      }
    }
  };

  const handleBrowse = () => fileInputRef.current?.click();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    // Reset so re-picking the same file still fires onChange.
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    // This drop is ours. The page behind the dialog runs its own external-file
    // drop zone, and React bubbles synthetic events up the React tree regardless
    // of the portal, so letting this through would stage the file here *and*
    // offer it to the page as a new document to file under a project.
    e.stopPropagation();
    setIsDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const move = (index: number, direction: -1 | 1) => {
    setItems((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const removeAt = (index: number) => setItems((prev) => prev.filter((_, i) => i !== index));

  const setMode = (key: string, mode: AddAsVersionMode) =>
    setItems((prev) =>
      prev.map((item) => (item.key === key && item.kind === "existing" ? { ...item, mode } : item))
    );

  const sortByName = () =>
    setItems((prev) =>
      [...prev].sort((a, b) => naturalCollator.compare(stagedItemName(a), stagedItemName(b)))
    );

  /** Every lineage in the list, with how many rows reference it. */
  const lineageCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of items) {
      if (item.kind !== "existing") continue;
      const key = lineageKeyOf(item.document);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [items]);

  const moveDisabledReason = (item: StagedVersionItem): string | null => {
    if (item.kind !== "existing") return null;
    if (!item.document.isCurrentVersion) return copy.moveDisabledOlderVersion;
    // A moved history can't also be copied from: the copy would read a row
    // that the move has already carried into the target.
    if ((lineageCounts.get(lineageKeyOf(item.document)) ?? 0) > 1) {
      return copy.moveDisabledDuplicate;
    }
    return null;
  };

  const projectName = (id: number | null): string | null =>
    id === null ? null : (projects.find((p) => Number(p.id) === id)?.name ?? null);

  // Projected numbers: each row takes the next `versionSpan` numbers.
  const projection = useMemo(() => {
    let running = baseVersion;
    return items.map((item) => {
      const first = running + 1;
      running += versionSpan(item);
      return { first, last: running };
    });
  }, [items, baseVersion]);

  const addedCount = items.reduce((sum, item) => sum + liveVersionSpan(item), 0);
  const waitingForHistory = items.some(
    (item) => item.kind === "existing" && item.mode === "move" && item.lineage === null
  );
  const blockedByHistory = items.some(
    (item) => item.kind === "existing" && item.mode === "move" && item.lineageFailed === true
  );

  const handleConfirm = async () => {
    if (items.length === 0) return;
    const staged = items;
    const { committed, touchedVersionGroups } = await onConfirm(staged);
    for (const group of touchedVersionGroups) {
      // Versioning reads these too, and decides on opening whether there is
      // anything to arrange; it must not decide on a list from before the add.
      void queryClient.invalidateQueries({ queryKey: lineageStatsKey(group) });
      void queryClient.invalidateQueries({ queryKey: versionHistoryKey(group) });
      void queryClient.invalidateQueries({ queryKey: lineageRowsKey(group) });
    }
    if (committed > 0) {
      // Keep only what did not land, so a retry picks up where it stopped.
      setItems((prev) => prev.filter((item) => !staged.slice(0, committed).includes(item)));
    }
  };

  if (!targetDocument) return null;

  const confirmLabel =
    items.length === 0
      ? copy.confirmNone
      : addedCount === 1
        ? copy.confirmSingle
        : copy.confirmMany.replace("{count}", String(addedCount));

  // Lineages already in the target's history, or staged to be moved into it,
  // can't be picked again.
  const unselectableGroups = [
    ...(targetVersionGroup ? [targetVersionGroup] : []),
    ...items.flatMap((item) =>
      item.kind === "existing" && item.mode === "move" && item.document.versionGroup
        ? [item.document.versionGroup]
        : []
    ),
  ];
  const stagedIds = items.flatMap((item) => (item.kind === "existing" ? [item.document.id] : []));
  // Closing drops the staged list, as it does from Versioning, so ask first.
  const requestClose = () => {
    if (isSubmitting) return;
    if (items.length > 0) setConfirmingClose(true);
    else onOpenChange(false);
  };
  const discardCopy = t.documents.versioning.manage;
  const footerActions = (
    <>
      <Button type="button" variant="outline" onClick={requestClose} disabled={isSubmitting}>
        {t.common.cancel}
      </Button>
      <Button
        type="button"
        onClick={handleConfirm}
        disabled={isSubmitting || items.length === 0 || waitingForHistory || blockedByHistory}
      >
        {isSubmitting ? copy.adding : confirmLabel}
      </Button>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : requestClose())}>
      <DialogContent
        className="max-w-3xl"
        // Drops that miss the drop zone (the list, the footer, the padding)
        // are still drops on this dialog, so they must not reach the page's zone.
        // preventDefault also stops the browser navigating to the dropped file.
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragOver={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>{copy.dialogTitle}</DialogTitle>
          <DialogDescription>
            {copy.dialogDescription
              .replace("{filename}", targetDocument.filename)
              .replace("{version}", String(targetDocument.version ?? baseVersion))}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className={cn("flex gap-3", items.length === 0 && "min-h-56")}>
            <button
              type="button"
              onClick={handleBrowse}
              disabled={isSubmitting}
              onDrop={handleDrop}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              className={cn(
                "flex-1 rounded-lg border-2 border-dashed p-6 text-center transition-colors",
                "disabled:cursor-not-allowed disabled:opacity-60",
                // With nothing staged the drop zone is the whole point of the
                // dialog, so let it fill the space the list would occupy.
                items.length === 0 && "flex flex-col items-center justify-center",
                isDragOver
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25 hover:border-primary hover:bg-primary/5"
              )}
            >
              <Upload className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
              <p className="font-medium">
                {isDragOver
                  ? copy.dropFilesHere
                  : items.length > 0
                    ? copy.addMoreFiles
                    : copy.dragDropFiles}
              </p>
              <p className="text-sm text-muted-foreground">{copy.orClickToBrowse}</p>
            </button>

            {canChooseExisting && (
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                disabled={isSubmitting}
                className={cn(
                  "flex w-48 shrink-0 flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center transition-colors",
                  "border-muted-foreground/25 hover:border-primary hover:bg-primary/5",
                  "disabled:cursor-not-allowed disabled:opacity-60"
                )}
              >
                <FolderSearch className="mb-3 h-8 w-8 text-muted-foreground" />
                <p className="font-medium">{copy.chooseFromDocuments}</p>
              </button>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />

          {items.length > 0 && (
            <>
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={sortByName}
                  disabled={isSubmitting || items.length < 2}
                >
                  <ArrowDownAZ className="mr-2 h-4 w-4" />
                  {copy.sortByName}
                </Button>
              </div>

              <TooltipProvider>
                <ul className="max-h-80 space-y-1 overflow-y-auto">
                  {items.map((item, index) => {
                    const crossProject =
                      item.kind === "existing" &&
                      item.mode === "move" &&
                      item.document.projectId !== null &&
                      item.document.projectId !== targetDocument.projectId;
                    return (
                      <StagedVersionRow
                        key={item.key}
                        item={item}
                        copy={copy}
                        firstVersion={projection[index].first}
                        lastVersion={projection[index].last}
                        isFirst={index === 0}
                        isLast={index === items.length - 1}
                        disabled={isSubmitting}
                        targetFilename={targetDocument.filename}
                        moveDisabledReason={moveDisabledReason(item)}
                        sourceProjectName={
                          item.kind === "existing" ? projectName(item.document.projectId) : null
                        }
                        crossProjectName={
                          crossProject && item.kind === "existing"
                            ? (projectName(item.document.projectId) ?? "")
                            : null
                        }
                        onMove={(direction) => move(index, direction)}
                        onRemove={() => removeAt(index)}
                        onModeChange={(mode) => setMode(item.key, mode)}
                      />
                    );
                  })}
                </ul>
              </TooltipProvider>
            </>
          )}
        </div>

        <DialogFooter className={cn(onBack && "gap-2 sm:justify-between")}>
          {onBack && (
            <Button type="button" variant="ghost" onClick={onBack} disabled={isSubmitting}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t.documents.versioning.manage.backToVersions}
            </Button>
          )}
          {/* Grouped only beside a back button: alone, they stay the footer's
              own children, which its flex-col-reverse stacks on narrow
              screens and its sibling spacing separates on wide ones. */}
          {onBack ? <div className="flex gap-2">{footerActions}</div> : footerActions}
        </DialogFooter>

        {canChooseExisting && (
          <DocumentPicker
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            buildingId={buildingId}
            mode="multi"
            showVersionHistory
            defaultProjectId={
              targetDocument.projectId !== null ? String(targetDocument.projectId) : undefined
            }
            alreadyAttachedIds={stagedIds}
            attachedLabel={copy.pickerAlreadyAdded}
            unselectableVersionGroups={unselectableGroups}
            unselectableReason={copy.pickerTargetLineage}
            title={copy.pickerTitle}
            description={copy.pickerDescription}
            submitLabel={copy.pickerSubmit}
            onSelect={handlePicked}
          />
        )}
        <AlertDialog open={confirmingClose} onOpenChange={setConfirmingClose}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{discardCopy.discardTitle}</AlertDialogTitle>
              <AlertDialogDescription>
                {discardCopy.discardStagedDescription}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{discardCopy.keepEditing}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setConfirmingClose(false);
                  onOpenChange(false);
                }}
              >
                {discardCopy.discard}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
